import {
  faArrowLeft,
  faArrowRight,
  faCircleXmark,
  faClockRotateLeft,
  faMagnifyingGlass,
  faSliders,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDismissOnOutside, useEscapeToClose } from "../../Hook/dismiss";
import { FormattedMessage, FormattedRelativeTime, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { fetchMembers } from "../../Api/member";
import { membersInformations_members } from "../../Api/types";
import useFilters from "../../Hook/filters";
import Avatar from "../Avatar";
import {
  ALL_FILTERS,
  GENDER,
  THERMOMETER,
  isActive,
  selectedOptions,
} from "../Filters/catalog";
import certified from "../Member/certified.svg";
import { addRecent, clearRecents, getRecents, removeRecent } from "./recents";

/**
 * 1a — search as a popup over the page. One entry point on each frame: a field that
 * lives in the desktop header (⌘K away from anywhere) and a magnifier in the mobile
 * one. It answers in place — recents first, then names as you type — and the last
 * row hands the query to the existing /search page, which stays the Enter destination.
 */

/** Live names for the query — debounced, stale answers dropped, filters optional. */
function useNameSearch(query: string, ignoreFilters: boolean) {
  const filters = useFilters();
  const [results, setResults] = useState<membersInformations_members[] | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      return;
    }
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      try {
        const members = await fetchMembers("search", {
          offset: 0,
          limit: 5,
          text: q,
          ...(ignoreFilters ? {} : filters.filters),
        });
        if (seq.current === mine) setResults(members);
      } catch {
        if (seq.current === mine) setResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, ignoreFilters, filters]);

  return results;
}

/** The active filters, spelled out the way the boxes spell them — labels, then "+n". */
function scopeParts(filters: any, intl: ReturnType<typeof useIntl>): string[] {
  const parts: string[] = [];
  let more = 0;
  for (const definition of ALL_FILTERS) {
    const value = filters?.[definition.key];
    if (!isActive(definition, value)) continue;
    if (definition === THERMOMETER || definition === GENDER || definition.key === "age_ranges") {
      parts.push(
        ...selectedOptions(definition, value).map((option) =>
          intl.formatMessage({ id: option.label }),
        ),
      );
    } else more++;
  }
  if (more > 0) parts.push(`+${more}`);
  return parts;
}

/**
 * The scope, stated rather than guessed at: a name that exists but is filtered out
 * is explained, and one tap searches without the filters instead.
 */
function ScopeLine({ ignore, setIgnore }: { ignore: boolean; setIgnore: (value: boolean) => void }) {
  const intl = useIntl();
  const filters = useFilters();
  const parts = scopeParts(filters.filters, intl);
  const scoped = parts.length > 0 && !ignore;

  return (
    <div className="flex items-center gap-2 px-4 py-2 min-w-0 flex-none">
      <FontAwesomeIcon
        icon={faSliders}
        className={"text-[12px] flex-none " + (scoped ? "text-accent" : "text-muted2")}
      />
      <div className="flex-1 min-w-0 text-explain text-muted truncate">
        {scoped ? (
          <>
            <FormattedMessage id="searchpopup__scope_filters" />
            {" · " + parts.join(" · ")}
          </>
        ) : (
          <FormattedMessage id="searchpopup__scope_all" />
        )}
      </div>
      {parts.length > 0 && (
        <button
          type="button"
          data-testid="search-scope-toggle"
          onClick={() => setIgnore(!ignore)}
          className="flex-none px-2.5 py-1 rounded-pill bg-tint-chip text-muted text-explain"
        >
          <FormattedMessage id={ignore ? "searchpopup__use_filters" : "searchpopup__ignore"} />
        </button>
      )}
    </div>
  );
}

/** The matched run wears the accent, so the answer shows why it answered. */
function Match({ pseudonym, query }: { pseudonym: string; query: string }) {
  const q = query.trim().toLowerCase();
  const at = q ? pseudonym.toLowerCase().indexOf(q) : -1;
  if (at < 0) return <>{pseudonym}</>;
  const end = at + q.length;
  return (
    <>
      {pseudonym.slice(0, at)}
      <span className="text-accent">{pseudonym.slice(at, end)}</span>
      {pseudonym.slice(end)}
    </>
  );
}

const Hairline = () => <div className="h-px bg-tint-neutral flex-none" />;

/**
 * A name row, not a member card — the panel stays a list of answers. Gender rides
 * the meta line instead of colouring the name, so the accent match stays legible.
 */
function ResultRow({
  member,
  query,
  surface,
  onOpen,
}: {
  member: membersInformations_members;
  query: string;
  /** The dot's ring matches the surface it punches out of. */
  surface: "card" | "panel";
  onOpen: () => void;
}) {
  const parts: React.ReactNode[] = [];
  if (["woman", "man", "couple"].includes(member.gender))
    parts.push(<FormattedMessage key="gender" id={"gender_sought__label__" + member.gender} />);
  if (member.age_range)
    parts.push(<FormattedMessage key="age" id={"age_range__value_label__" + member.age_range} />);
  if (member.cityInformations) parts.push(member.cityInformations.name);
  if (!member.is_online && member.time_online !== undefined)
    parts.push(
      <FormattedRelativeTime
        key="time"
        value={member.time_online - Date.now() / 1000}
        numeric="auto"
      />,
    );

  return (
    <div
      onClick={onOpen}
      data-testid="search-result-row"
      className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-tint-hover"
    >
      <div className="relative w-[38px] h-[38px] flex-none">
        <div className="w-full h-full rounded-avatar overflow-hidden bg-well">
          <Avatar member={member} />
        </div>
        {member.is_online && (
          <span
            className={
              "absolute right-0 bottom-0 w-2.5 h-2.5 rounded-full bg-online ring-2 " +
              (surface === "card" ? "ring-card" : "ring-panel")
            }
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-row font-medium text-text min-w-0">
          <span className="truncate">
            <Match pseudonym={member.pseudonym} query={query} />
          </span>
          {member.is_certified && (
            <img className="h-[15px] flex-none" src={certified} alt="certified" />
          )}
        </div>
        {parts.length > 0 && (
          <div className="text-explain text-muted truncate">
            {parts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="opacity-40"> · </span>}
                {part}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RecentRow({
  query,
  onPick,
  onRemove,
}: {
  query: string;
  onPick: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      onClick={onPick}
      data-testid="search-recent-row"
      className="flex items-center gap-3 px-4 py-[9px] cursor-pointer hover:bg-tint-hover"
    >
      <FontAwesomeIcon icon={faClockRotateLeft} className="text-[14px] text-muted2 flex-none" />
      <div className="flex-1 min-w-0 text-row text-text truncate">{query}</div>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        className="flex-none px-1 text-muted2 hover:text-text"
      >
        <FontAwesomeIcon icon={faXmark} className="text-[13px]" />
        <span className="sr-only">
          <FormattedMessage id="searchpopup__remove_recent" />
        </span>
      </button>
    </div>
  );
}

/** The hand-off row: whatever the panel can't show, the members feed can. */
function FeedRow({ query, hint, onFeed }: { query: string; hint?: boolean; onFeed: () => void }) {
  return (
    <div
      onClick={onFeed}
      data-testid="search-feed-row"
      className="flex items-center gap-2.5 px-4 py-3 cursor-pointer text-accent hover:bg-tint-hover flex-none"
    >
      <FontAwesomeIcon icon={faUsers} className="text-[14px] flex-none" />
      <div className="flex-1 min-w-0 text-row font-medium truncate">
        <FormattedMessage id="searchpopup__see_in_feed" values={{ query: query.trim() }} />
      </div>
      {hint ? (
        <span className="flex-none text-[11px] font-mono text-muted2">↵</span>
      ) : (
        <FontAwesomeIcon icon={faArrowRight} className="text-[12px] flex-none" />
      )}
    </div>
  );
}

/**
 * What both frames show under the field: recents while idle, names while typing,
 * and the feed row to conclude either way.
 */
function PanelBody({
  query,
  ignore,
  surface,
  onPickRecent,
  onOpenMember,
  onFeed,
}: {
  query: string;
  ignore: boolean;
  surface: "card" | "panel";
  onPickRecent: (query: string) => void;
  onOpenMember: (member: membersInformations_members) => void;
  onFeed: () => void;
}) {
  const results = useNameSearch(query, ignore);
  const [recents, setRecents] = useState(getRecents);
  const searching = query.trim().length >= 2;

  if (searching)
    return (
      <>
        <Hairline />
        <div className="flex flex-col py-1.5 overflow-y-auto min-h-0">
          {results && results.length === 0 && (
            <div className="px-4 py-3 text-row text-muted">
              <FormattedMessage id="search__no_result" />
            </div>
          )}
          {(results ?? []).map((member) => (
            <ResultRow
              key={member.id}
              member={member}
              query={query}
              surface={surface}
              onOpen={() => onOpenMember(member)}
            />
          ))}
        </div>
        <Hairline />
        <FeedRow query={query} hint={surface === "card"} onFeed={onFeed} />
      </>
    );

  if (recents.length === 0) return null;
  return (
    <>
      <Hairline />
      <div className="flex items-baseline justify-between px-4 pt-2.5 pb-1 flex-none">
        <div className="text-kicker tracking-kicker uppercase font-mono text-muted2">
          <FormattedMessage id="searchpopup__recent" />
        </div>
        <button
          type="button"
          data-testid="search-recent-clear"
          onClick={() => setRecents(clearRecents())}
          className="text-explain text-accent"
        >
          <FormattedMessage id="searchpopup__clear" />
        </button>
      </div>
      <div className="flex flex-col pb-1.5 overflow-y-auto min-h-0">
        {recents.map((recent) => (
          <RecentRow
            key={recent}
            query={recent}
            onPick={() => onPickRecent(recent)}
            onRemove={() => setRecents(removeRecent(recent))}
          />
        ))}
      </div>
    </>
  );
}

/** The field (260) plus the slot's margin and a little breathing room. */
const FIELD_ROOM = 300;

/**
 * Whether the header has room for the field at all. Not a breakpoint question:
 * the header's inner row is capped at max-w-7xl, so past 1280px the answer stops
 * depending on the window — it depends on the brand's logo, the locale's labels
 * and whether the member is signed in. So the room is computed from geometry:
 * where the nav's content actually ends, and what the blocks right of the search
 * slot actually take. The slot itself is excluded, which makes the answer the
 * same whichever variant is currently rendered — no feedback, no flapping.
 */
function useHeaderRoom(setFolded: (value: boolean) => void, locale: string) {
  const outerWidth = (el: Element) => {
    const style = getComputedStyle(el);
    return (
      el.getBoundingClientRect().width +
      (parseFloat(style.marginLeft) || 0) +
      (parseFloat(style.marginRight) || 0)
    );
  };
  const measure = () => {
    const inner = document.querySelector('[data-testid="app-header"]')
      ?.firstElementChild as HTMLElement | null;
    const slot = document.querySelector('[data-testid="header-search-slot"]');
    if (!inner || !slot) return;
    const children = [...inner.children];
    const nav = children[0];
    // The nav is flex-1 and grows over any slack, so its box width says nothing —
    // its content's right edge does. (Its fixed-positioned drawer sits off-screen
    // left, so the max is always a real in-row edge.)
    const navUsed =
      Math.max(...[...nav.children].map((c) => c.getBoundingClientRect().right)) -
      inner.getBoundingClientRect().left;
    const rightUsed = children
      .slice(1)
      .filter((c) => c !== slot)
      .reduce((sum, c) => sum + outerWidth(c), 0);
    setFolded(inner.clientWidth - navUsed - rightUsed < FIELD_ROOM);
  };

  // No dependency list on purpose: whatever re-renders the header (login state,
  // avatar arriving, locale) re-measures before the frame paints.
  useLayoutEffect(() => {
    measure();
  });
  useLayoutEffect(() => {
    window.addEventListener("resize", measure);
    // The nav's children move without a React render too — the brand logo image
    // loading is the one that matters.
    const nav = document.querySelector('[data-testid="app-header"]')?.firstElementChild
      ?.firstElementChild;
    const observer = new ResizeObserver(measure);
    if (nav) for (const child of nav.children) observer.observe(child);
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [locale]);
}

/**
 * Desktop — the field is persistent in the header and the panel opens under it,
 * over the dimmed page. Enter is the fallback door to /search, carrying the query.
 *
 * A logged-in header carries the full nav, Publier, the avatar and both pickers,
 * and the row is capped at max-w-7xl — when the field genuinely doesn't fit, it
 * folds back to the same magnifier the mobile header uses (measured, not a
 * breakpoint — see useHeaderRoom).
 */
export function DesktopSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [ignore, setIgnore] = useState(false);
  const [folded, setFolded] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const intl = useIntl();
  useHeaderRoom(setFolded, intl.locale);

  const close = () => {
    setOpen(false);
    input.current?.blur();
  };

  // Two keystrokes from anywhere — ⌘K, or Ctrl K off a Mac. When the field is
  // folded away (offsetParent null) the magnifier variant owns the shortcut.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        if (!input.current?.offsetParent) return;
        event.preventDefault();
        setOpen(true);
        input.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useDismissOnOutside(open, root, close);

  const openMember = (member: membersInformations_members) => {
    addRecent(query);
    close();
    navigate("/member/" + member.id);
  };
  const goFeed = () => {
    const q = query.trim();
    if (!q) return;
    addRecent(q);
    close();
    navigate("/search?q=" + encodeURIComponent(q));
  };

  const mac = navigator.userAgent.includes("Mac");

  if (folded) return <MobileSearch shortcut />;

  return (
    <div className="relative flex items-center" ref={root}>
      {/* The page dims under the header, so the panel answers over it, not beside it. */}
      {open && (
        <div
          className="fixed left-0 right-0 bottom-0 top-16 bg-tint-overlay z-popover"
          data-testid="search-overlay"
          onClick={close}
        />
      )}
      <div
        className={
          "relative z-popover-panel w-[260px] h-9 rounded-pill bg-tint-neutral flex items-center gap-2 px-3.5 " +
          (open ? "ring-1 ring-tint-border-accent" : "")
        }
      >
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className={"text-[13px] flex-none " + (open ? "text-accent" : "text-muted")}
        />
        <input
          ref={input}
          value={query}
          data-testid="header-search-field"
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") goFeed();
          }}
          placeholder={intl.formatMessage({ id: "searchpopup__placeholder" })}
          // The legacy App.css `input { color: white !important }` still applies —
          // the important text token wins it back, the way the chat composer does.
          className="flex-1 min-w-0 bg-transparent outline-none text-row text-text placeholder:text-muted"
        />
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              input.current?.focus();
            }}
            className="flex-none text-muted2 hover:text-text"
          >
            <FontAwesomeIcon icon={faCircleXmark} className="text-[14px]" />
            <span className="sr-only">
              <FormattedMessage id="searchpopup__clear" />
            </span>
          </button>
        ) : (
          <span className="flex-none text-[10.5px] font-mono text-muted2">
            {mac ? "⌘K" : "Ctrl K"}
          </span>
        )}
      </div>
      {open && (
        <div
          data-testid="header-search-panel"
          className="absolute right-0 top-12 z-popover-panel w-[400px] max-h-[70vh] flex flex-col rounded-panel bg-card shadow-panel overflow-hidden py-1"
        >
          <ScopeLine ignore={ignore} setIgnore={setIgnore} />
          <PanelBody
            query={query}
            ignore={ignore}
            surface="card"
            onPickRecent={(recent) => {
              setQuery(recent);
              input.current?.focus();
            }}
            onOpenMember={openMember}
            onFeed={goFeed}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Mobile — a magnifier in the header opens the panel over whatever was on screen.
 * Idle it hangs under the top of the screen with the page dimmed behind; once a
 * query is live the results take the full height.
 */
export function MobileSearch({ shortcut }: { shortcut?: boolean }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [ignore, setIgnore] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const intl = useIntl();

  const close = () => {
    setOpen(false);
    setQuery("");
    setIgnore(false);
  };

  useEscapeToClose(open ? close : undefined);

  // The folded desktop entry keeps the shortcut — but only while its magnifier is
  // the visible one, so the two variants never both answer a ⌘K.
  useEffect(() => {
    if (!shortcut) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        if (!trigger.current?.offsetParent) return;
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [shortcut]);

  const openMember = (member: membersInformations_members) => {
    addRecent(query);
    close();
    navigate("/member/" + member.id);
  };
  const goFeed = () => {
    const q = query.trim();
    if (!q) return;
    addRecent(q);
    close();
    navigate("/search?q=" + encodeURIComponent(q));
  };

  const searching = query.trim().length >= 2;

  return (
    <>
      <button
        type="button"
        ref={trigger}
        onClick={() => setOpen(true)}
        data-testid="header-search-open"
        className="flex items-center justify-center w-9 h-9 flex-none self-center rounded-avatar bg-tint-pill text-accent"
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} className="text-[15px]" />
        <span className="sr-only">
          <FormattedMessage id="menu__search" />
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-popover-panel flex flex-col"
          role="dialog"
          aria-modal="true"
          data-testid="header-search-panel"
        >
          <div className="absolute inset-0 bg-tint-overlay" onClick={close} />
          <div
            className={
              // On a desktop-wide window (the folded header entry) the sheet reads
              // as a centred palette instead of a full-bleed screen.
              "relative flex flex-col bg-panel shadow-sheet min-h-0 w-full desktop:max-w-[560px] desktop:self-center desktop:rounded-b-sheet " +
              (searching ? "flex-1 desktop:flex-none desktop:max-h-[80vh]" : "rounded-b-sheet max-h-[80vh]")
            }
          >
            <div className="flex items-center gap-1.5 pl-1.5 pr-3 pt-2 pb-2 flex-none">
              <button
                type="button"
                onClick={close}
                className="w-10 h-10 flex-none flex items-center justify-center text-text"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-[18px]" />
                <span className="sr-only">
                  <FormattedMessage id="searchpopup__close" />
                </span>
              </button>
              <div className="flex-1 min-w-0 h-[42px] rounded-pill bg-tint-neutral flex items-center gap-2 px-3.5">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className={"text-[15px] flex-none " + (searching ? "text-accent" : "text-muted")}
                />
                <input
                  ref={input}
                  autoFocus
                  value={query}
                  data-testid="header-search-field"
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") goFeed();
                  }}
                  placeholder={intl.formatMessage({ id: "searchpopup__placeholder" })}
                  // The legacy App.css `input { color: white !important }` still applies —
                  // the important text token wins it back, the way the chat composer does.
                  className="flex-1 min-w-0 bg-transparent outline-none text-row text-text placeholder:text-muted"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      input.current?.focus();
                    }}
                    className="flex-none text-muted2"
                  >
                    <FontAwesomeIcon icon={faCircleXmark} className="text-[15px]" />
                    <span className="sr-only">
                      <FormattedMessage id="searchpopup__clear" />
                    </span>
                  </button>
                )}
              </div>
            </div>
            <ScopeLine ignore={ignore} setIgnore={setIgnore} />
            <PanelBody
              query={query}
              ignore={ignore}
              surface="panel"
              onPickRecent={(recent) => {
                setQuery(recent);
                input.current?.focus();
              }}
              onOpenMember={openMember}
              onFeed={goFeed}
            />
          </div>
        </div>
      )}
    </>
  );
}
